/**
 * PassengersCard - Expandable card for passenger/special personnel
 */

import React from 'react';
import { UserPlus, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const PassengersCard = ({ 
  passengers,
  setPassengers,
  isExpanded,
  onToggle,
  isComplete
}) => {
  return (
    <div className={`rounded-lg border-2 transition-all ${
      isComplete ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
    }`}>
      <div 
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserPlus className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-gray-600'}`} />
            <div>
              <h4 className="text-sm font-medium">Passengers / Special Personnel</h4>
              <p className="text-xs text-gray-500">
                {passengers.hasPassengers ? `${passengers.count} passengers` : 'None'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isComplete && <CheckCircle className="h-5 w-5 text-green-600" />}
            {isExpanded ? 
              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
              <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={passengers.hasPassengers}
                onChange={(e) => setPassengers(prev => ({
                  ...prev,
                  hasPassengers: e.target.checked
                }))}
                className="rounded"
              />
              <span className="text-sm">Carrying passengers/special personnel</span>
            </label>

            {passengers.hasPassengers && (
              <>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> Workboats can carry up to 12 passengers without being classified 
                    as a passenger vessel. Above 12 requires passenger vessel certification.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Passengers
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="12"
                    value={passengers.count}
                    onChange={(e) => {
                      const count = parseInt(e.target.value) || 0;
                      if (count > 12) {
                        alert('More than 12 passengers requires passenger vessel certification');
                        return;
                      }
                      setPassengers(prev => ({
                        ...prev,
                        count,
                        names: Array(count).fill('').map((_, i) => prev.names[i] || '')
                      }));
                    }}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>

                {passengers.count > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Passenger Names (Optional)
                    </label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {Array(passengers.count).fill(0).map((_, idx) => (
                        <input
                          key={idx}
                          type="text"
                          value={passengers.names[idx] || ''}
                          onChange={(e) => {
                            const newNames = [...passengers.names];
                            newNames[idx] = e.target.value;
                            setPassengers(prev => ({
                              ...prev,
                              names: newNames
                            }));
                          }}
                          placeholder={`Passenger ${idx + 1}`}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengersCard;